import { AudioBus } from './AudioBus';
import { assetUrl } from './assetUrl';

/** Crossfading ambient music player for The Quiet Keep. */

export type MusicMood =
  | 'courtyard'
  | 'climb'
  | 'companion'
  | 'bridge'
  | 'gate'
  | 'tension'
  | 'hope'
  | 'story'
  | 'ambient';

export type TrackInfo = {
  id: string;
  mood: MusicMood | string;
  file: string;
  title: string;
};

const CROSSFADE = 3.2;

export class MusicSystem {
  private bus = AudioBus.get();
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private tracks: TrackInfo[] = [];
  private buffers = new Map<string, AudioBuffer>();
  private current: { id: string; source: AudioBufferSourceNode; gain: GainNode } | null = null;
  private started = false;
  private unlocked = false;
  private preferMood: MusicMood | null = null;
  private lastSwitch = 0;
  private rotateTimer = 0;
  private loadPromise: Promise<void> | null = null;

  async loadCatalog(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = (async () => {
      const res = await fetch(assetUrl('audio/music/tracks.json'));
      this.tracks = (await res.json()) as TrackInfo[];
    })();
    return this.loadPromise;
  }

  getTrackList(): TrackInfo[] {
    return this.tracks.slice();
  }

  /** Call from a user gesture to satisfy autoplay policy. */
  async unlock(): Promise<void> {
    await this.loadCatalog();
    this.ctx = await this.bus.unlock();
    this.master = this.bus.musicGain;
    this.unlocked = true;
  }

  async start(mood: MusicMood = 'courtyard'): Promise<void> {
    await this.unlock();
    this.bus.setTitleBed(false);
    const wasPreview = this.started;
    this.started = true;
    this.preferMood = mood;
    this.lastSwitch = 0;
    await this.playForMood(mood, true, wasPreview);
  }

  /** Quiet bed under the title screen (before full start). */
  async startTitlePreview(): Promise<void> {
    await this.unlock();
    this.bus.setTitleBed(true);
    if (!this.started) {
      this.started = true;
      this.preferMood = 'ambient';
      await this.playForMood('ambient', true);
    }
  }

  setMood(mood: MusicMood): void {
    if (!this.started || !this.unlocked) {
      this.preferMood = mood;
      return;
    }
    if (this.preferMood === mood) return;
    this.preferMood = mood;
    const now = performance.now() / 1000;
    if (now - this.lastSwitch < 12) return;
    void this.playForMood(mood, false);
  }

  update(dt: number): void {
    if (!this.started || !this.unlocked) return;
    this.rotateTimer += dt;
    // Soft rotate within mood every ~90s so loops don't feel stuck
    if (this.rotateTimer > 90 && this.preferMood) {
      this.rotateTimer = 0;
      void this.playForMood(this.preferMood, false, true);
    }
  }

  private pickTrack(mood: MusicMood, forceDifferent: boolean): TrackInfo | null {
    if (!this.tracks.length) return null;
    const matching = this.tracks.filter((t) => t.mood === mood);
    const pool = matching.length ? matching : this.tracks.filter((t) => t.mood === 'ambient' || t.mood === 'courtyard');
    const usable = pool.length ? pool : this.tracks;
    let candidates = usable;
    if (forceDifferent && this.current && usable.length > 1) {
      candidates = usable.filter((t) => t.id !== this.current!.id);
    }
    return candidates[Math.floor(Math.random() * candidates.length)] ?? usable[0];
  }

  private async ensureBuffer(track: TrackInfo): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    const cached = this.buffers.get(track.id);
    if (cached) return cached;
    try {
      const res = await fetch(assetUrl(track.file));
      const arr = await res.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(arr.slice(0));
      this.buffers.set(track.id, buf);
      return buf;
    } catch (err) {
      console.warn('Music decode failed', track.id, err);
      return null;
    }
  }

  private async playForMood(mood: MusicMood, immediate: boolean, forceDifferent = false): Promise<void> {
    if (!this.ctx || !this.master) return;
    const track = this.pickTrack(mood, forceDifferent || !immediate);
    if (!track) return;
    if (this.current && this.current.id === track.id && !forceDifferent) return;

    const buffer = await this.ensureBuffer(track);
    if (!buffer || !this.ctx || !this.master) return;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    // Slight loop cross inside the buffer edges (already faded in render)
    source.loopStart = 2;
    source.loopEnd = Math.max(3, buffer.duration - 2);

    const gain = this.ctx.createGain();
    const now = this.ctx.currentTime;
    if (immediate || !this.current) {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(1, now + 1.4);
    } else {
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(1, now + CROSSFADE);
    }

    source.connect(gain);
    gain.connect(this.master);
    source.start(0);

    if (this.current) {
      const prev = this.current;
      prev.gain.gain.cancelScheduledValues(now);
      prev.gain.gain.setValueAtTime(prev.gain.gain.value, now);
      prev.gain.gain.linearRampToValueAtTime(0, now + CROSSFADE);
      const old = prev;
      window.setTimeout(() => {
        try {
          old.source.stop();
        } catch {
          /* already stopped */
        }
        old.source.disconnect();
        old.gain.disconnect();
      }, (CROSSFADE + 0.2) * 1000);
    }

    this.current = { id: track.id, source, gain };
    this.lastSwitch = performance.now() / 1000;
    this.rotateTimer = 0;
  }
}

/** Infer music mood from player position and story flags. */
export function moodFromState(opts: {
  x: number;
  z: number;
  held: boolean;
  gateOpen: boolean;
  nearCompanion: boolean;
  nearLever: boolean;
  climbing: boolean;
}): MusicMood {
  if (opts.gateOpen) return 'hope';
  if (opts.nearLever) return 'gate';
  if (opts.climbing) return 'climb';
  if (opts.held) {
    if (opts.z > 6 && opts.z < 13) return 'bridge';
    return 'companion';
  }
  if (opts.nearCompanion) return 'companion';
  if (opts.z > 8) return 'bridge';
  if (Math.abs(opts.x) > 5 && opts.z < 4) return 'climb';
  return 'courtyard';
}
